import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ApiCallService } from '../../Services/api-call-service.service';
import { ErrorhandlingService } from '../../Services/error-handling.service';
import { GameCardComponent } from '../../components/game-card/game-card.component';
import { GameService } from '../../Services/game.service';
import { LoaderService } from '../../Services/loader-service.service';
import { LoginComponent } from '../login/login.component';

interface QuickGameCard {
  id: number;
  name: string;
  image: string;
  redirectLink: string;
  bgclr: string;
  color2: string;
  isSystem: boolean;
  isHot: boolean;
  provider: string;
}

@Component({
  selector: 'app-quick-games',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, GameCardComponent, LoginComponent],
  templateUrl: './quick-games.component.html',
  styleUrl: './quick-games.component.scss',
})
export class QuickGamesComponent implements OnInit {
  searchTerm: string = '';
  allGames: QuickGameCard[] = [];
  loading: boolean = false;
  showLoginModal: boolean = false;

  constructor(
    private gameService: GameService,
    private apiCallService: ApiCallService,
    private loaderService: LoaderService,
    private errorHandling: ErrorhandlingService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    this.loadGames();
    this.loadHotOffers();
  }

  private get isToken(): boolean {
    return isPlatformBrowser(this.platformId) && !!localStorage.getItem('token');
  }

  /**
   * Mirrors games-landing: when logged in, the token-based
   * `GetHotHittingGames` endpoint is the source of truth for the HOT tag.
   * Patches `isHot` on the already-loaded cards, keyed by normalized name.
   */
  private loadHotOffers(): void {
    if (!this.isToken) return;

    this.apiCallService
      .GetCallWithToken('TestImonials/GetHotHittingGames')
      .subscribe({
        next: (response: any) => {
          if (response?.responseCode !== 200 || !Array.isArray(response.data)) {
            return;
          }

          const hotByName = new Map<string, boolean>();
          response.data.forEach((offer: any) => {
            hotByName.set(this.normalize(offer.GameName), !!offer.IsHot);
          });

          this.allGames = this.allGames.map((g) => ({
            ...g,
            isHot: hotByName.get(this.normalize(g.name)) ?? g.isHot,
          }));

          this.loaderService.setArrayInLocalStorage('bis_offer', response.data);
        },
        error: (error) => {
          console.error('Failed to load hot hitting games', error);
        },
      });
  }

  get filteredGames(): QuickGameCard[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.allGames;
    return this.allGames.filter(
      (g) =>
        g.name.toLowerCase().includes(term) ||
        g.provider?.toLowerCase().includes(term)
    );
  }

  private normalize(name: string): string {
    return (name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private resolveGameData(apiName: string): any {
    const gameData = [
      ...this.gameService.getGames(),
      ...this.gameService.SystemGames,
    ];
    const apiNormalized = this.normalize(apiName);

    return (
      gameData.find((g: any) => this.normalize(g.name) === apiNormalized) ||
      gameData.find((g: any) =>
        this.normalize(g.name).includes(apiNormalized)
      ) ||
      gameData.find((g: any) =>
        apiNormalized.includes(this.normalize(g.name))
      ) ||
      null
    );
  }

  private mapToCard(game: any, isSystem: boolean): QuickGameCard {
    return {
      id: game.id ?? 0,
      name: game.name,
      image: game.image || game.coverImage || '/Images/placeholder.png',
      redirectLink: game.downloadLink || '#',
      bgclr: game.bgclr || '#00dbde',
      color2: game.color2 || '#3333ff',
      isSystem,
      isHot: !!game.isHot,
      provider: game.provider || '',
    };
  }

  private loadGames(): void {
    this.loading = true;
    this.loaderService.show();

    const systemGames = this.gameService.SystemGames.map((g: any) =>
      this.mapToCard(g, true)
    );

    const homeGames = this.gameService
      .getGames()
      .filter((g: any) => this.normalize(g.name) !== 'slots')
      .map((g: any) => this.mapToCard(g, false));

    this.allGames = this.mergeUnique([...systemGames, ...homeGames]);

    this.apiCallService.GetCallWithoutToken('Public/GetGames').subscribe({
      next: (response: any) => {
        if (response?.responseCode === 200 && Array.isArray(response.data)) {
          // API is the source of truth for `isHot` — key it by normalized name.
          const hotByName = new Map<string, boolean>();
          response.data.forEach((g: any) => {
            if (g?.isHot) hotByName.set(this.normalize(g.name), true);
          });

          const cmaxGames = response.data
            .filter((g: any) => this.normalize(g.name) !== 'slots')
            .map((g: any) => {
              const matched = this.resolveGameData(g.name);
              const game = matched
                ? { ...matched, name: g.name || matched.name, isHot: g.isHot }
                : g;
              return this.mapToCard(game, false);
            });

          this.allGames = this.mergeUnique([
            ...systemGames,
            ...homeGames,
            ...cmaxGames,
          ]).map((g) => ({
            ...g,
            isHot: hotByName.get(this.normalize(g.name)) ?? g.isHot,
          }));
        }
        this.loading = false;
        this.loaderService.hide();
      },
      error: (error) => {
        this.loading = false;
        this.loaderService.hide();
        console.error('Failed to load CMax games', error);
      },
    });
  }

  private mergeUnique(games: QuickGameCard[]): QuickGameCard[] {
    const seen = new Set<string>();
    return games.filter((g) => {
      const key = this.normalize(g.name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  onSearch(): void {
    // reactive via getter
  }

  getStatus(game: QuickGameCard): { label: string; active: boolean } {
    if (game.isSystem || game.isHot) {
      return { label: 'ACTIVE', active: true };
    }
    return { label: 'PLAY', active: false };
  }

  playGame(game: QuickGameCard): void {
    if (isPlatformBrowser(this.platformId) && !localStorage.getItem('token')) {
      this.showLoginModal = true;
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('GN', game.name);
    }

    const offerData = this.gameService.getArrayInLocalStorage('bis_offer');
    const matchedOffer = offerData.find(
      (g: any) => g.GameName === game.name
    );

    if (matchedOffer?.IsOnUpdate === 'True') {
      this.errorHandling.showAlert(
        'warning',
        'Game is under update!. Please try again later.'
      );
      return;
    }

    this.gameService.setGameData({
      GameName: game.name,
      GameId: game.id,
      IsOnUpdate: matchedOffer?.IsOnUpdate || 'False',
    });

    this.router.navigate(['/dashboard/credentials']);
  }

  closeLoginModal(): void {
    this.showLoginModal = false;
  }
}
